import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Row, Col, Container } from 'reactstrap';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { TerraceInputsVM } from './TerraceInputsVM';
import { Input } from 'cd-eft/components/common/Input/Input';
import { ComboBox } from 'cd-eft/components/common/ComboBox';
import './TerraceInputs.scss';
import { ButtonBlock } from '../../common/ButtonBlock/ButtonBlock';
import { ActionsMenu } from '../../common/ActionsMenu';
import { storageProps, subbasinRunoffProps } from './TerraceInputs.labels.config';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { VegetalCoverComponent } from 'cd-eft/components/EFH2/Waterway Sizing Calulator/VegetalCoversComp/VegetalCovers';
import { useHistory, useParams } from 'react-router-dom';
import stores from 'cd-eft/models/Stores';

const { terraceDesignStore, designStore } = stores.AppStore;

const TerraceInputsComponent = () => {
  const history = useHistory();
  const { terraceId }: any = useParams();

  const vmRef = useRef<TerraceInputsVM | null>(null);
  if (!vmRef.current) {
    vmRef.current = new TerraceInputsVM(history);
  }
  
  const vm = vmRef.current;
  
  const {
    TerraceInfoSection,
    AlignmentSection,
    HydrologySection,
    SoilStrengthSection,
    StorageSection,
  } = vm.terraceInputsConfig;

  useEffect(() => {
    if (vm) {
      terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId = +terraceId;
      vm.initializeDropdownInputsAndRestoreData();
      terraceDesignStore.terraceHistory  = history
    }
  }, []);

  return (
    <Container>
      <Row>
        <Col xs="6">
          <Accordion
            className={'terrace-info accordion-wrapper'}
            headers={['Info']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className='info-container sub-container'>
              {Object.entries(TerraceInfoSection).map(([key, config]) => {
                return (
                  <Row key={'info-row-' + key}>
                    <Col>
                      <TerraceInputForm
                        key={'info-input-' + key}
                        config={config}
                        value={vm.inputForms['TerraceInfoSection'][key]}
                        onChange={(e) => vm.handleInputsOnChange(e, 'TerraceInfoSection')}
                        onBlur={(e) => vm.handleInputsOnBlur(e, 'TerraceInfoSection')}
                        red={vm.redHighlighting['TerraceInfoSection'][key]}
                      />
                    </Col>
                  </Row>
                )
              })}
            </div>
          </Accordion>
        </Col>
        <Col xs="6">
          <Accordion
            className={'terrace-alignment accordion-wrapper'}
            headers={['Alignment']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className='alignment-container sub-container'>
              {Object.entries(AlignmentSection).map(([key, config]) => (
                <Row key={'alignment-row-' + key}>
                  <Col>
                    <TerraceInputForm
                      key={'alignment-input-' + key}
                      config={config}
                      value={vm.inputForms['AlignmentSection'][key]}
                      onChange={(e) => vm.handleInputsOnChange(e, 'AlignmentSection')}
                      onBlur={(e) => vm.handleInputsOnBlur(e, 'AlignmentSection')}
                      red={vm.redHighlighting['AlignmentSection'][key]}
                    />

                  </Col>
                </Row>
              ))}
            </div>
          </Accordion>
          <Accordion
            className={'terrace-hydrology accordion-wrapper'}
            headers={['Subbasin Hydrology']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className='hydrology-container sub-container'>
              <Row>
                <Col xs={12} className='p-0'>
                  {Object.entries(HydrologySection).map(([key, config]) => (
                    <Row key={'subbasin-row-' + key}>
                      <Col>
                        <TerraceInputForm
                          key={'subbasin-input-' + key}
                          config={config}
                          value={vm.inputForms['HydrologySection'][key]}
                          onChange={(e) => vm.handleInputsOnChange(e, 'HydrologySection')}
                          onBlur={(e) => vm.handleInputsOnBlur(e, 'HydrologySection')}
                          red={vm.redHighlighting['HydrologySection'][key]}
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>
              <button className='usa-button sub-calculate-button' onClick={() => vm.calculateSubbasinResults()} disabled={vm.disableHydrologyCalculateButton}>Calculate</button>
              {vm.enableCalculationResults.subbasinRunoff && <ResultsContainer calculationResults={vm.hydroResults} {...subbasinRunoffProps} />}
            </div>
          </Accordion>
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          <Accordion
            className={'terrace-soil-strength accordion-wrapper'}
            headers={['Soil Strength']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className='soil-strength-container sub-container'>
              {Object.entries(SoilStrengthSection).map(([key, config]) => (
                <Row key={'soil-strength-row-' + key}>
                  <Col>
                    <TerraceInputForm
                      key={'soil-strength-input-' + key}
                      config={config}
                      value={vm.inputForms['SoilStrengthSection'][key]}
                      onChange={(e) => vm.handleInputsOnChange(e, 'SoilStrengthSection')}
                      onBlur={(e) => vm.handleInputsOnBlur(e, 'SoilStrengthSection')}
                      red={vm.redHighlighting['SoilStrengthSection'][key]}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          </Accordion>
        </Col>
      </Row>
      <Row>
        <Col>
          <Accordion
            className={'terrace-vegetal-cover accordion-wrapper'}
            headers={['Vegetal Cover']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className="vegetal-cover-container sub-container">
              <VegetalCoverComponent vm={vm} />
            </div>
          </Accordion>
        </Col>
      </Row>
      { !vm.isStorageSectionHidden && <Row>
        <Col>
          <Accordion
            className={'terrace-storage accordion-wrapper'}
            headers={['Runoff & Sediment Storage']}
            waterwayWizardStatus={1}
            onClickInformationToggle={vm.onClickInformationToggle}
            light={true}
            iconPathQ={true}
          >
            <div className='storage-container sub-container'>
              <Row>
                <Col xs="6">
                  {Object.entries(StorageSection)
                    .slice(0, 3)
                    .map(([key, config]) => (
                      <Row key={'runoff-row-' + key}>
                        <Col className='p-0'>
                          <TerraceInputForm
                            key={'runoff-input-' + key}
                            config={config}
                            value={vm.inputForms['StorageSection'][key]}
                            onChange={(e) => vm.handleInputsOnChange(e, 'StorageSection')}
                            onBlur={(e) => vm.handleInputsOnBlur(e, 'StorageSection')}
                            red={vm.redHighlighting['StorageSection'][key]}
                          />
                        </Col>
                      </Row>
                    ))}
                </Col>

                <Col xs="6">
                  {Object.entries(StorageSection)
                    .slice(3)
                    .map(([key, config]) => (
                      <Row key={key}>
                        <Col className='p-0'>
                          <TerraceInputForm
                            key={key}
                            config={config}
                            value={vm.inputForms['StorageSection'][key]}
                            onChange={(e) => vm.handleInputsOnChange(e, 'StorageSection')}
                            onBlur={(e) => vm.handleInputsOnBlur(e, 'StorageSection')}
                            red={vm.redHighlighting['StorageSection'][key]}
                          />
                        </Col>
                      </Row>
                    ))}
                </Col>
              </Row>
              <button className='usa-button sub-calculate-button' onClick={() => vm.calculateSedimenAndRunoffStorageResults()} disabled={vm.disableRunoffSedimentCalculateButton}>Calculate</button>
              {vm.enableCalculationResults.storage && vm.sedimentAndRunoffStorageResults && <ResultsContainer {...storageProps} calculationResults={vm.sedimentAndRunoffStorageResults} />}
            </div>
          </Accordion>
        </Col>
      </Row> }
      <Row>
        <Col>
          <Accordion
            className={'terrace-comments accordion-wrapper'}
            headers={['Comments']}
            actionButton={<ActionsMenu i={'terrace-comments'} item="item" elementActions={vm.elementActions}>More</ActionsMenu>}
            light={true}
          >
            <div>

            </div>
          </Accordion>
        </Col>
      </Row>
      <Row className='calculate-buttons-block'>
        <Col>
          <hr />
          <ButtonBlock buttons={[
            { title: 'Close', disabled: false, onClick: terraceDesignStore.handlecloseAction, className: 'usa-button--outline' },
            { title: 'Save', disabled: vm.disableSaveButton, onClick: ()=>{vm.onClickSave('save')} },
            { title: 'Save & Proceed', disabled: vm.disableSaveAndProceedButton, onClick: ()=>{vm.onClickSave('saveAndProceed')} },
          ]} />
        </Col>
      </Row>

      {vm.loading && <Loader message={''} />}
    </Container>
  );
};

export const TerraceInputs = observer(TerraceInputsComponent);


export const TerraceInputForm = observer(({ config, value, onChange, onBlur, red}) => {
  const { label, inputType, options, className, readonly = false, disabled = false, hidden = false, decimalPlaces = 2 } = config;
  const step = Math.pow(10, -decimalPlaces);
  return (
    <>
      {(!hidden) && (inputType === 'text' || inputType === 'number') && (
        <Input
          className={red ? 'usa-input--error' : '' }
          idAndName={className}
          valid={true}
          value={value}
          type={inputType}
          onChange={onChange || (() => { })}
          onBlur={onBlur || (() => { })}
          label={label || ''}
          wrapperClassName={`${className}-input`}
          readOnly={readonly}
          disabled={disabled}
          step={step}
        />
      )}
      {inputType === 'dropdown' && (
        <ComboBox
          className={red ? 'usa-input--error' : ''}
          label={label}
          idAndName={className}
          valid={true}
          value={value}
          onChange={onChange || (() => { })}
          onBlur={onBlur || (() => { })}
          options={options || []}
          errorMsg=''
          disabled={disabled}
        />
      )}
      {inputType === 'title' && (
        <label className={`${className} usa-label`}>
          <b>{label}</b>
        </label>
      )}
    </>
  );
})

interface ResultItem {
  readonly label: string;
  readonly key: string;
}

interface ResultGroup {
  readonly title: string;
  readonly key: string;
  readonly items: readonly ResultItem[];
}

interface IResultsContainerProps {
  readonly rowCols: number;
  readonly extraClass: string;
  readonly resultGroups: readonly ResultGroup[];
  readonly calculationResults: any;
}

// Modified <ResultsContainer> from formula
export const ResultsContainer = ({
  rowCols,
  extraClass,
  resultGroups,
  calculationResults
}: IResultsContainerProps) => {
  return (
    <div className="usa-summary-box formula-results-container" role="region">
      <div className="results-container-content usa-summary-box__body">
        <div className="results-summary-heading">Results</div>
        <div className={`row results-value-list' ${extraClass || ''}`}>
          {resultGroups.map((group, index) => (
            <div key={index} className="col result-group">
              <div className="subheading">{group.title}</div>
              {group.items.map((item, idx) => {
                let value;
                value = calculationResults?.[item.key];
                if (typeof value === 'number') {
                  value = value.toFixed(2);
                }
                return (
                  <div key={idx} className="result-item">
                    <div className="result-label">{item.label}:</div>
                    <div className="result-value">{value !== undefined ? value : "N/A"}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



